import { ref } from 'vue';

// Define the structure for saved scroll position
type SavedScrollPosition = { left: number; top: number };

// Reactive ref to hold the pending scroll information
// Stores the target path and the scroll position to restore
const pendingScroll = ref<{ path: string; position: SavedScrollPosition } | null>(null);

/**
 * Composable to manage scroll position restoration state between router and page component.
 */
export function usePendingScrollStore() {

  /**
   * Stores the scroll position intended for a specific path.
   * This is typically called by the router's scrollBehavior when savedPosition is available.
   * @param path - The full path of the route to restore scroll for.
   * @param position - The saved scroll position ({ top, left }).
   */
  const setPendingScroll = (path: string, position: SavedScrollPosition) => {
    // console.log(`[PendingScrollStore] Setting pending scroll for ${path}:`, position);
    pendingScroll.value = { path, position };
  };

  /**
   * Retrieves and consumes (clears) the pending scroll position for the given path.
   * This is typically called by the page component (e.g., in onMounted) after checking
   * if it should restore scroll.
   * @param currentPath - The full path of the currently active route.
   * @returns The saved scroll position if it matches the current path, otherwise null.
   */
  const consumePendingScroll = (currentPath: string): SavedScrollPosition | null => {
    if (pendingScroll.value && pendingScroll.value.path === currentPath) {
      const pos = pendingScroll.value.position;
      // console.log(`[PendingScrollStore] Consuming pending scroll for ${currentPath}:`, pos);
      pendingScroll.value = null; // Consume it - only restore once
      return pos;
    }
    return null;
  };

  return {
    setPendingScroll,
    consumePendingScroll,
    // Expose the ref directly for debugging or watching if needed
    _pendingScrollRef: pendingScroll
  };
} 